import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { Button } from './button';
import { CategorySelector } from './partners/category-selector';
import { createStyles } from '@/contexts/theme-context';
import { ItemGroupSelector } from './item-group-selector';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { ItemSearchInput } from './item-search-input';
import { LIST_HEIGHT } from './horizontal-selector';
import { SearchCatalogContent } from './search-catalog-content';
import { SearchParams } from '@/hooks/use-search-variant-packs';
import { TYPOGRAPHY } from '@/styles';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useItemGroupCategoryFilters } from '@/hooks/use-item-group-category-filters';
import { useLayoutContext } from '@/contexts/layout-context';
import { useSearchCatalog } from '@/hooks/use-search-catalog';
import { View } from 'react-native';

interface Props {
  children?: React.ReactNode;
  emptySearchContent?: React.ReactNode;
  filterTrackingKey: string;
  isInboundOrder?: boolean;
  isLoading?: boolean;
  isModal?: boolean;
  onClose?: () => void;
  renderActions: (
    sku: ItemListOptionMetadata,
    source: 'list' | 'sheet',
    searchResults: ReturnType<typeof useSearchCatalog>,
  ) => React.ReactNode;
  searchParams: SearchParams;
  showVariantPackOrderInfo?: boolean;
  skipListInteractionCheck?: boolean;
}

export const CatalogReplacementSearch: React.FC<Props> = ({
  children,
  emptySearchContent,
  filterTrackingKey,
  isInboundOrder,
  isLoading,
  isModal,
  onClose,
  renderActions,
  searchParams,
  showVariantPackOrderInfo,
  skipListInteractionCheck = false,
}) => {
  const [styles] = useStyles();
  const { isSmallLayout } = useLayoutContext();
  const searchCatalogValue = useSearchCatalog(searchParams);
  const [searchInputValue, setSearchInputValue] = useState<string | null>(null);

  const {
    availabilityMap,
    changeSortOrder,
    clearSearch,
    items,
    queryLoading,
    queryRef,
    searchItems,
    searchSortOrder,
    categories,
    categoryFilter,
    setCategoryFilter,
    categoryItemGroupFilter,
    setCategoryItemGroupFilter,
    categoryItemGroups,
  } = searchCatalogValue;

  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');

  const { categoryItemGroupsInSelector } = useItemGroupCategoryFilters({
    categoryUUIDsToFilterFrom: categoryItemGroups.map(
      (itemGroup) => itemGroup.uuid!,
    ),
  });

  return (
    <View style={styles.listContainer}>
      <View style={styles.searchInputRow}>
        <ItemSearchInput
          clearSearch={clearSearch}
          containerStyle={styles.searchInputContainer}
          initialSearchText={searchParams.initialSearchText}
          loading={isLoading || queryLoading}
          onKeyPress={(e) => {
            if (e.nativeEvent.key === 'Escape') {
              onClose?.();
            }
          }}
          onSearchItems={searchItems}
          onSearchTextChange={setSearchInputValue}
          placeholder={i18n.t('search_item_placeholder')}
        />
        {onClose ? (
          <Button
            onPress={onClose}
            textStyle={TYPOGRAPHY.h4SemiBold}
            trackingData={{
              name: 'catalog_replacement_search.close',
            }}
            type="tertiary"
          >
            {i18n.t('close')}
          </Button>
        ) : null}
      </View>
      {children}
      {itemGroupsEnabled ? (
        categoryItemGroupsInSelector.length ? (
          <View style={styles.categoryContainerView}>
            <ItemGroupSelector
              containerStyle={
                isSmallLayout ? null : styles.categoryContainerLarge
              }
              data={categoryItemGroupsInSelector}
              onItemGroupPress={setCategoryItemGroupFilter}
              selectedItemGroup={categoryItemGroupFilter}
              trackingKey={filterTrackingKey}
            />
          </View>
        ) : null
      ) : categories.length ? (
        <View style={styles.categoryContainerView}>
          <CategorySelector
            categories={categories}
            onCategoryPress={setCategoryFilter}
            selectedCategory={categoryFilter}
            style={isSmallLayout ? null : styles.categoryContainerLarge}
          />
        </View>
      ) : null}
      {emptySearchContent && !searchInputValue && !queryLoading ? (
        emptySearchContent
      ) : (
        <SearchCatalogContent
          availabilityMap={availabilityMap}
          changeSortOrder={changeSortOrder}
          enforceSingleColumn
          isInboundOrder={isInboundOrder}
          isModal={isModal}
          items={items}
          listItemSeparatorStyle={styles.listItemSeparator}
          loading={isLoading || queryLoading}
          renderActions={(sku, source) =>
            renderActions(sku, source, searchCatalogValue)
          }
          renderSource={searchParams.searchSource}
          searchSortOrder={searchSortOrder}
          searchText={queryRef.current}
          showVariantPackOrderInfo={showVariantPackOrderInfo}
          skipListInteractionCheck={skipListInteractionCheck}
        />
      )}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  categoryContainerLarge: {
    paddingHorizontal: 16,
  },
  categoryContainerView: {
    borderColor: theme.colors.stroke,
    borderTopWidth: 1,
    height: LIST_HEIGHT,
  },
  listContainer: {
    flex: 1,
    overflow: 'hidden',
  },
  listItemSeparator: {
    marginHorizontal: 16,
  },
  searchInputContainer: {
    backgroundColor: theme.colors.background,
    borderRadius: 8,
    flex: 1,
  },
  searchInputRow: {
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'center',
    marginBottom: 8,
    marginHorizontal: 16,
  },
}));
